import React, { createContext, useCallback, useContext, useState } from "react";

/**
 * Tanzmodus: filtert Inhalte nach `dance_type` und steuert sichtbare Produktkopie.
 *
 * Persistenz: Modus in localStorage; Standard ist **Hustle** (`latin_hustle`) — auch beim ersten
 * App-Besuch ohne gespeicherte Auswahl. Content-Filter nutzt den Slug (`dance_type` in der DB).
 *
 * QuickStart: früher wurde hier kurz `salsa` erzwungen — das leerte „Neues entdecken“, sobald
 * `user_choreography_videos.dance_type` auf Slugs wie `latin_hustle` umgestellt war. Jetzt gilt
 * der gewählte Tanzmodus (wie im Rest der App).
 */
export type DanceMode =
  | "salsa"
  | "latin_hustle"
  | "bachata"
  | "zouk"
  | "west_coast_swing"
  | "country_swing"
  | "urban_kiz";

/** Auswahl-Reihenfolge im Top-Menü (eine Klick-Ziele). */
export const ALL_DANCE_MODES: DanceMode[] = [
  "latin_hustle",
  "salsa",
  "bachata",
  "zouk",
  "west_coast_swing",
  "country_swing",
  "urban_kiz",
];

const DANCE_MODE_STORAGE_KEY = "coupleup-dance-mode";

/** Alte gespeicherte Werte → neuer Modus (Migration). */
const LEGACY_STORED_TO_MODE: Record<string, DanceMode> = {
  hustle: "latin_hustle",
  lindy_hop: "salsa",
  salsa: "salsa",
  latin_hustle: "latin_hustle",
  bachata: "bachata",
  zouk: "zouk",
  west_coast_swing: "west_coast_swing",
  country_swing: "country_swing",
  urban_kiz: "urban_kiz",
};

/**
 * Filter-Wert für Content-Tabellen (`dance_type`): gleicher Slug wie `dance_styles.slug` / `DanceMode`.
 */
export function danceSlugForContentFilter(mode: DanceMode): string {
  return mode;
}

/** Vergleich gespeicherter `dance_type` aus der DB mit aktuellem Tanz-Slug. */
export function danceTypesMatchStored(
  dbDanceType: string | null | undefined,
  mode: DanceMode
): boolean {
  return (dbDanceType ?? "") === mode;
}

/**
 * Slug für RPCs / JSONB (entspricht `dance_styles.slug` und Keys in `*_by_dance`).
 */
export function danceSlugForRpc(mode: DanceMode): string {
  return mode;
}

function getStoredDanceMode(): DanceMode {
  try {
    const stored = localStorage.getItem(DANCE_MODE_STORAGE_KEY);
    if (stored && stored in LEGACY_STORED_TO_MODE) {
      return LEGACY_STORED_TO_MODE[stored]!;
    }
  } catch {
    // localStorage nicht verfügbar (z. B. privater Modus mit blockiertem Storage)
  }
  return "latin_hustle";
}

interface DanceModeContextValue {
  danceMode: DanceMode;
  setDanceMode: (mode: DanceMode) => void;
  /** true genau dann wenn danceMode === "west_coast_swing" (Rückwärtskompatibilität). */
  isWestCoastSwing: boolean;
}

const DanceModeContext = createContext<DanceModeContextValue | null>(null);

/** Nur Lesen: effektiver Modus für Kinder (z. B. QuickStart → Haupt-Pool). */
const DanceModeReadOverrideContext = createContext<DanceMode | null>(null);

/**
 * Früher: Salsa-Override für QuickStart. Ohne Override nutzen Kinder denselben `danceMode` wie
 * überall — nötig damit `usePublicChoreographyVideos` / `useOwnSessionVideos` nach `latin_hustle`
 * filtern, wenn der Nutzer Hustle gewählt hat.
 */
export function QuickStartDanceModeOverride({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  return <>{children}</>;
}

export const DanceModeProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [danceMode, setDanceModeState] = useState<DanceMode>(getStoredDanceMode);

  const setDanceMode = useCallback((mode: DanceMode) => {
    setDanceModeState(mode);
    try {
      // Standard = Hustle: kein Eintrag (wie früher bei Salsa)
      if (mode === "latin_hustle") {
        localStorage.removeItem(DANCE_MODE_STORAGE_KEY);
      } else {
        localStorage.setItem(DANCE_MODE_STORAGE_KEY, mode);
      }
    } catch {
      // localStorage nicht verfügbar
    }
  }, []);

  const value: DanceModeContextValue = {
    danceMode,
    setDanceMode,
    isWestCoastSwing: danceMode === "west_coast_swing",
  };

  return (
    <DanceModeContext.Provider value={value}>
      {children}
    </DanceModeContext.Provider>
  );
};

export function useDanceMode() {
  const context = useContext(DanceModeContext);
  const readOverride = useContext(DanceModeReadOverrideContext);
  if (!context) {
    throw new Error("useDanceMode must be used within DanceModeProvider");
  }
  const danceMode = readOverride ?? context.danceMode;
  return {
    ...context,
    danceMode,
    isWestCoastSwing: danceMode === "west_coast_swing",
  };
}
